import { Matrix3 } from "three";
// NOTE: This representation uses the same order as the Matrix3 class from three.js
export class mat3 {
    static toThreeMatrix3(m) {
        // prettier-ignore
        return m ? new Matrix3().set(m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]) : new Matrix3().identity();
    }
    static makeIdentity() {
        // prettier-ignore
        return [
            1, 0, 0,
            0, 1, 0,
            0, 0, 1
        ];
    }
    static makeTranslation(x, y) {
        // prettier-ignore
        return [
            1, 0, 0,
            0, 1, 0,
            x, y, 1
        ];
    }
    static makeRotation(theta) {
        // counterclockwise
        const c = Math.cos(theta);
        const s = Math.sin(theta);
        // prettier-ignore
        return [
            c, s, 0,
            -s, c, 0,
            0, 0, 1
        ];
    }
    static makeScale(x, y) {
        // prettier-ignore
        return [
            x, 0, 0,
            0, y, 0,
            0, 0, 1,
        ];
    }
    /** Checks if the matrix is a simple affine transformation with 0,0,1 on the bottom row */
    static affineSanityCheck(m3) {
        return m3[2] === 0 && m3[5] === 0 && m3[8] === 1;
    }
    static decompose(m3, equalScalingThreshold = 0.001) {
        // https://stackoverflow.com/a/45392997
        const xyDot = m3[0] * m3[3] + m3[1] * m3[4];
        const rotation = -Math.atan2(m3[3], m3[0]);
        const translation = { x: m3[6], y: m3[7] };
        const scaleX = Math.sqrt(m3[0] ** 2 + m3[1] ** 2);
        const scaleY = Math.sqrt(m3[3] ** 2 + m3[4] ** 2);
        const scale = { x: scaleX, y: scaleY };
        const scaleEquality = Math.abs(1 - scale.x / scale.y);
        const scaleIsEqual = scaleEquality <= equalScalingThreshold;
        return { rotation, xyDot, translation, scale, scaleEquality, scaleIsEqual };
    }
    static matrix3To4(m3) {
        // prettier-ignore
        return [
            m3[0], m3[1], 0, m3[2],
            m3[3], m3[4], 0, m3[5],
            0, 0, 1, 0,
            m3[6], m3[7], 0, m3[8],
        ];
    }
    // prettier-ignore
    static multiply(a, b) {
        const te = [0, 0, 0, 0, 0, 0, 0, 0, 0];
        const a11 = a[0], a12 = a[3], a13 = a[6];
        const a21 = a[1], a22 = a[4], a23 = a[7];
        const a31 = a[2], a32 = a[5], a33 = a[8];
        const b11 = b[0], b12 = b[3], b13 = b[6];
        const b21 = b[1], b22 = b[4], b23 = b[7];
        const b31 = b[2], b32 = b[5], b33 = b[8];
        te[0] = a11 * b11 + a12 * b21 + a13 * b31;
        te[3] = a11 * b12 + a12 * b22 + a13 * b32;
        te[6] = a11 * b13 + a12 * b23 + a13 * b33;
        te[1] = a21 * b11 + a22 * b21 + a23 * b31;
        te[4] = a21 * b12 + a22 * b22 + a23 * b32;
        te[7] = a21 * b13 + a22 * b23 + a23 * b33;
        te[2] = a31 * b11 + a32 * b21 + a33 * b31;
        te[5] = a31 * b12 + a32 * b22 + a33 * b32;
        te[8] = a31 * b13 + a32 * b23 + a33 * b33;
        return te;
    }
    // prettier-ignore
    static invert(m) {
        const n11 = m[0], n21 = m[1], n31 = m[2];
        const n12 = m[3], n22 = m[4], n32 = m[5];
        const n13 = m[6], n23 = m[7], n33 = m[8];
        const t11 = n33 * n22 - n32 * n23;
        const t12 = n32 * n13 - n33 * n12;
        const t13 = n23 * n12 - n22 * n13;
        const det = n11 * t11 + n21 * t12 + n31 * t13;
        if (det === 0)
            return [0, 0, 0, 0, 0, 0, 0, 0, 0];
        const detInv = 1 / det;
        const te = [0, 0, 0, 0, 0, 0, 0, 0, 0];
        te[0] = t11 * detInv;
        te[1] = (n31 * n23 - n33 * n21) * detInv;
        te[2] = (n32 * n21 - n31 * n22) * detInv;
        te[3] = t12 * detInv;
        te[4] = (n33 * n11 - n31 * n13) * detInv;
        te[5] = (n31 * n12 - n32 * n11) * detInv;
        te[6] = t13 * detInv;
        te[7] = (n21 * n13 - n23 * n11) * detInv;
        te[8] = (n22 * n11 - n21 * n12) * detInv;
        return te;
    }
    static transpose(m) {
        return [m[0], m[3], m[6], m[1], m[4], m[7], m[2], m[5], m[8]];
    }
}
//# sourceMappingURL=mat3.js.map